/**
 * Onboarding (demo `Onboarding`, `OB`, `SURVEY`, `uiBtns`, `courseBtns`): 8 qadam, har savol alohida ekranda.
 * Tab ichida emas — `.content` + `.sfoot` ni o'zi chizadi. Profil «Soʻrovnoma javoblarim» → `obStart.step = 1` + `set({onb:false})`.
 */
import { useState } from 'react';
import { COURSES, LANG, W, shuffle } from '../data';
import type { Course } from '../data/types';
import { useStore, type Mode, type Survey } from '../store';
import { switchCourse } from '../store';
import { nav } from '../lib/nav';
import { t, tn, UI, UI_LANGS, setUiLang } from '../i18n';
import { fire, sync } from '../lib/sync';
import { Ic } from '../ui/Icon';
import { Brand } from '../ui/Brand';
import { clampGoal } from './lesson/engine';

export const SURVEY: Record<'src' | 'age' | 'status' | 'why', { q: string; o: string[] }> = {
  src: { q: 'Lexo haqida qayerdan eshitdingiz?', o: ['Telegram', 'Instagram', 'Tanishim / Yaqinim', 'Oʻquv markazi', 'Google qidiruv', 'YouTube'] },
  age: { q: 'Yoshingiz nechada?', o: ['14 dan kichik', '14–17', '18–24', '25–34', '35 va undan katta'] },
  status: { q: 'Hozir nima bilan bandsiz?', o: ['Maktab oʻquvchisi', 'Talaba', 'Ishlayman', 'Ishlamayman / uydaman', 'Boshqa'] },
  why: { q: 'Bu til sizga nima uchun kerak?', o: ['IELTS / CEFR imtihoni', 'Chet elda oʻqish', 'Ish / karyera', 'Sayohat', 'Oʻzim uchun'] },
};
export const OB = ['name', 'src', 'age', 'mode', 'status', 'why', 'level', 'goal'] as const;
type ObKey = (typeof OB)[number];
const isSurvey = (k: ObKey): k is keyof typeof SURVEY => k in SURVEY;

/** Boshlang'ich qadam (demo `ob.step`): Profil so'rovnomani 1-qadamdan qayta ochish uchun `obStart.step = 1` qiladi. */
export const obStart = { step: 0 };

/** Demo `uiBtns` — interfeys tili segmenti (uz/ru/en). */
export function UiBtns() {
  return (
    <div className="uiseg" role="group" aria-label={t('Ilova tili')}>
      {UI_LANGS.map(([k, l]) => (
        <button key={k} aria-pressed={UI === k} onClick={() => { if (k === UI) return; setUiLang(k); }}>{l}</button>
      ))}
    </div>
  );
}

/** Demo `courseBtns` — o'rganilayotgan til kartalari. */
export function CourseBtns({ onPick }: { onPick: (k: Course) => void }) {
  return (
    <>
      {COURSES.map(({ k, code, name, desc }) => {
        const on = LANG === k;
        return (
          <button key={k} className="course" aria-pressed={on} onClick={() => onPick(k)}>
            <span className="badge" style={{ background: on ? 'var(--primary-soft)' : 'var(--bg)', color: on ? 'var(--primary-ink)' : 'var(--muted)' }}>{code}</span>
            <span className="grow col g4"><b className="t16b">{t(name)}</b><span className="t13">{t(desc)}</span></span>
            {on && <span className="ck"><Ic n="check" /></span>}
          </button>
        );
      })}
    </>
  );
}

/** [daraja, rang, nom, izoh] — so'z jangida raqib shu daraja bo'yicha. */
export const LEVELS: Array<[string, string, string, string]> = [
  ['A1', 'success', 'Noldan boshlayman', 'Eng kerakli soʻzlar'], ['A2', 'info', 'Oddiy gaplarni tushunaman', 'Kundalik mavzular'], ['B1', 'primary', 'Bemalol gaplasha olaman', 'Ish, sayohat, oʻqish'],
];

const GOALS: Array<[number, string]> = [[3, '≈ 5 daqiqa · Yengil'], [5, '≈ 8 daqiqa · Meʼyorida'], [7, '≈ 12 daqiqa · Jiddiy'], [10, '≈ 15 daqiqa · Intensiv']];

export function Onboarding() {
  const [step, setStep] = useState(obStart.step);
  const name = useStore((s) => s.name);
  const survey = useStore((s) => s.survey);
  const mode = useStore((s) => s.mode);
  const goal = clampGoal(useStore((s) => s.goal));
  const lvl = useStore((s) => s.lvl);
  const set = useStore((s) => s.set);
  const key = OB[step] ?? 'name';

  const patchSurvey = (p: Partial<Survey>) => set({ survey: { ...useStore.getState().survey, ...p } });
  const go = () => {
    if (step < OB.length - 1) { setStep(step + 1); return; }
    const s = useStore.getState();
    const order = s.order && s.order.length ? s.order : shuffle(W.map((w) => w.id));
    obStart.step = 0;
    s.set({ onb: true, ui: 'uz', survey: { ...s.survey, at: new Date().toISOString() }, order });
    nav.home('home');
    const sv = s.survey;
    fire(sync.me({ name: s.name, mode: s.mode ?? 'free', center_name: sv.center ?? '', onboarded: true, survey: { src: sv.src, age: sv.age, status: sv.status, why: sv.why } })
      .then(() => sync.course({ daily_goal: s.goal, order, level: s.lvl })), 'onboarding');
  };
  const pickMode = (m: Mode) => {
    set({ mode: m, survey: { ...useStore.getState().survey, learn: m } });
    if (m === 'free') setTimeout(go, 220);
  };

  let body: React.ReactNode; let can = true; let label = 'Davom etish';
  if (key === 'name') {
    body = (
      <>
        <UiBtns />
        <div className="col g14">
          <Brand className="logo" />
          <h1 className="h2">{t('Lexoʼga xush kelibsiz')}</h1>
          <p className="sub">{t('Har kuni 10 daqiqa — soʻz boyligingiz oʻsib boradi.')}</p>
        </div>
        <div className="col g10">
          <label className="cap" htmlFor="nm">{t('Ismingiz')}</label>
          <div className="field"><input id="nm" autoComplete="given-name" defaultValue={name} maxLength={24} onInput={(e) => set({ name: e.currentTarget.value.trim() })} /></div>
        </div>
        <div className="col g10">
          <div className="cap">{t('Qaysi tilni oʻrganasiz?')}</div>
          <CourseBtns onPick={(k) => switchCourse(k)} />
        </div>
      </>
    );
  } else if (isSurvey(key)) {
    const s = SURVEY[key]; const v = survey[key]; can = !!v;
    body = (
      <>
        <div className="col g8"><span className="cap">{t(`${step}-savol`)}</span><h1 className="h2">{t(s.q)}</h1><p className="sub">{t('Bittasini tanlang.')}</p></div>
        <div className="col g8">
          {s.o.map((o) => (
            <button key={o} className="goal opt1" aria-pressed={v === o} onClick={() => { patchSurvey({ [key]: o }); setTimeout(go, 220); }}>
              <b>{t(o)}</b>{v === o && <span className="ck"><Ic n="check" /></span>}
            </button>
          ))}
        </div>
      </>
    );
  } else if (key === 'mode') {
    can = !!mode;
    body = (
      <>
        <div className="col g8"><span className="cap">{t(`${step}-savol`)}</span><h1 className="h2">{t('Qanday oʻrganasiz?')}</h1><p className="sub">{t('Shunga qarab darslar tartibini moslaymiz.')}</p></div>
        <div className="col g10">
          <button className="course" aria-pressed={mode === 'free'} onClick={() => pickMode('free')}>
            <span className="badge" style={{ background: 'var(--info-soft)', color: 'var(--info-ink)' }}><Ic n="zap" /></span>
            <span className="grow col g4"><b className="t16b">{t('Mustaqil oʻrganaman')}</b><span className="t13">{t('Lexo soʻzlarni oʻzi tanlab, tasodifiy tartibda beradi')}</span></span>
            {mode === 'free' && <span className="ck"><Ic n="check" /></span>}
          </button>
          <button className="course" aria-pressed={mode === 'center'} onClick={() => pickMode('center')}>
            <span className="badge" style={{ background: 'var(--peach-soft)', color: 'var(--peach-ink)' }}><Ic n="book" /></span>
            <span className="grow col g4"><b className="t16b">{t('Oʻquv markazida oʻqiyman')}</b><span className="t13">{t('Har darsdagi soʻzlarni oʻz kartochkangizga qoʻshib oʻrganasiz')}</span></span>
            {mode === 'center' && <span className="ck"><Ic n="check" /></span>}
          </button>
        </div>
        {mode === 'center' && (
          <div className="col g8">
            <label className="cap" htmlFor="cn">{t('Oʻquv markazi nomi (ixtiyoriy)')}</label>
            <div className="field"><input id="cn" defaultValue={survey.center ?? ''} maxLength={40} onInput={(e) => patchSurvey({ center: e.currentTarget.value.trim() })} /></div>
          </div>
        )}
      </>
    );
  } else if (key === 'level') {
    body = (
      <>
        <div className="col g8"><h1 className="h2">{t(`${LANG === 'ru' ? 'Rus' : 'Ingliz'} tilini qanchalik bilasiz?`)}</h1><p className="sub">{t('Shunga mos soʻzlardan boshlaymiz.')}</p></div>
        <div className="col g10">
          {LEVELS.map(([lv, c, n, d]) => (
            <button key={lv} className="course" aria-pressed={lvl === lv} onClick={() => set({ lvl: lv, survey: { ...useStore.getState().survey } })}>
              <span className="badge" style={{ background: `var(--${c}-soft)`, color: `var(--${c}-ink)` }}>{lv}</span>
              <span className="grow col g4"><b className="t16b">{t(n)}</b><span className="t13">{t(d)}</span></span>
              {lvl === lv && <span className="ck"><Ic n="check" /></span>}
            </button>
          ))}
        </div>
        <p className="t12" style={{ margin: 0 }}>{t('Darslar shu darajadagi soʻzlardan boshlanadi, soʻz jangida raqiblar ham shu daraja boʻyicha tanlanadi. Keyin Toʻplamlar boʻlimida almashtirish mumkin.')}</p>
      </>
    );
  } else {
    label = 'Boshlash';
    body = (
      <>
        <div className="col g8"><h1 className="h2">{t('Kuniga nechta yangi soʻz?')}</h1><p className="sub">{t('Har dars shuncha yangi soʻz beradi, takrorlashlar bunga qoʻshimcha. Keyin Sozlamalarda 3 dan 10 gacha oʻzgartirish mumkin.')}</p></div>
        <div className="col g8">
          {GOALS.map(([n, d]) => (
            <button key={n} className="goal" aria-pressed={goal === n} onClick={() => set({ goal: n })}><b>{tn('# ta soʻz', n)}</b><span>{t(d)}</span></button>
          ))}
        </div>
        {mode === 'center'
          ? <div className="note" style={{ background: 'var(--primary-soft)', color: 'var(--primary-ink)' }}><Ic n="book" /><span>{t('Bilib qoʻying: oʻquv markazidagi har dars soʻzlarini alohida kartochkaga qoʻshib oʻrganish imkoniyati bor (Toʻplamlar → Kartochkalarim). Bu ixtiyoriy.')}</span></div>
          : <div className="note"><Ic n="bell" /><span>{t('Eslatmalar mobil ilovada boʻladi.')}</span></div>}
      </>
    );
  }

  return (
    <>
      <div className="content fade" key={step} style={{ padding: '24px 24px 20px', gap: 22 }}>
        <div className="steps">{OB.map((_, i) => <i key={i} className={i <= step ? 'on' : ''} />)}</div>
        {body}
      </div>
      <div className="sfoot" style={{ paddingInline: 24 }}>
        {step > 0 && <button className="btn soft sq" aria-label={t('Orqaga')} onClick={() => setStep(step - 1)}><Ic n="back" /></button>}
        <button className="btn" disabled={!can} onClick={go}>{can ? t(label) : t('Javobni tanlang')}</button>
      </div>
    </>
  );
}
